

































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import VSwitchButton from "@/components/VSwitchButton.vue";
import GranularitySwitcher from "./GranularitySwitcher.vue";
import InvestorsMixin from "@/mixins/http/Investor";
import Dictionary from "@/interfaces/Dictionary";
import { Brand, Broadcaster, Company, Model, Product, User } from "@/models";
import IntervalCount from "@/interfaces/IntervalCount";
import CustomChart, { CustomChartItem } from "@/components/CustomChart.vue";
import PieChart from "@/components/PieChart.vue";
import BroadcasterBreakdownTable, {
  BreakdownAgencyData,
  BreakdownBroadcasterData,
  BreakdownData,
  BreakdownDataItem,
} from "@/components/Broadcaster/BreakdownTable.vue";
import GranularityHelper from "@/services/GranularityHelper";
import MediaValue from "@/interfaces/MediaValue";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import Disclaimer from "@/components/GrpTargets/Disclaimer.vue";
import { AdcontrolProfile } from "@/models";
import { Getter } from "vuex-class";
import NoCache from "@/decorators/NoCache";
import ClientUserInfo from "@/models/Agents/UserInfo";
import { ProfileCapabilitiesService } from "@/services/Profile/CapabilitiesService";

interface BreakdownTableRequestFilter {
  media: {
    radio: boolean;
    tv: boolean;
  };
  date: {
    from: string;
    to: string;
  };
  ready: boolean;
  entry: Company | Brand | Product | null;
  spots?: string[];
}

export interface BreakdownTableFilter extends BreakdownTableRequestFilter {
  grouped: boolean;
}

export interface BreakdownTotals {
  spots: number;
  seconds: number;
  broadcasters: number;
  vodBroadcasters: number;
  agencies: number;
  grp: number;
  grpOverSpots: number;
  ebiquityMin: number;
  ebiquityMax: number;
}

interface Settings {
  grouped: boolean;
  unitOfMeasure: string;
  granularity: GranularityHelper;
}

const requestTag = "breakdown";

@Component({
  components: {
    Disclaimer,
    VSwitchButton,
    GranularitySwitcher,
    CustomChart,
    PieChart,
    BroadcasterBreakdownTable,
    MultiLevelSelect,
  },
})
export default class BreakdownTable extends Mixins(InvestorsMixin) {
  @Prop() readonly filter!: BreakdownTableFilter | null;
  @Prop({ required: true }) readonly media!: MediaValue;
  @Prop({ default: "spots" }) readonly unitOfMeasure!: string;
  @Prop({ required: true })
  readonly unitOfMeasureOptions!: Array<MultiLevelObject>;
  @Prop({ default: true }) readonly charts?: boolean;
  @Prop({ default: null }) readonly grpTargetId!: string;

  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Getter("agentUserInfos") readonly agentUserInfo?: ClientUserInfo;
  @Getter("user") readonly user!: User;

  protected currentFilter: BreakdownTableRequestFilter = {
    media: { radio: true, tv: true },
    date: { from: "", to: "" },
    ready: false,
    entry: null,
  };
  protected grouped = false;

  @Watch("unitOfMeasure")
  onUnitChange(value: string) {
    this.settings.unitOfMeasure = value;
  }

  @Watch("filter", { deep: true, immediate: true })
  onFilterChange(value: BreakdownTableFilter) {
    if (!value) return;

    const { media, date, ready } = value;
    if (this.filter && media && date && ready) {
      this.currentFilter.ready = ready;
      this.currentFilter.media.radio = media.radio;
      this.currentFilter.media.tv = media.tv;
      this.currentFilter.date.from = date.from;
      this.currentFilter.date.to = date.to;
      if (date.from && date.to) {
        this.settings.granularity.updateRange(date.from, date.to);
      }

      this.grouped = this.filter.grouped;
    }

    if (this.filter) this.load();
  }

  @Watch("media", { deep: true, immediate: true })
  onMediaChange(value: MediaValue) {
    if (
      this.filter &&
      (this.filter.media.tv !== value.tv ||
        this.filter.media.radio !== value.radio)
    ) {
      this.filter.media = value;
    }
  }

  @Watch("settings", { deep: true, immediate: true })
  onSettingChanged(value: Settings) {
    this.$emit("settings-changed", value);
  }

  @Watch("grpTargetId")
  onGrpTargetIdChanged(value: string | null) {
    if (value) {
      this.load();
    }
  }

  get onlyVodProfile() {
    if (this.profile?.mediaSourceKind == "msk_vod") {
      return true;
    }
    return false;
  }

  get showVodDisclaimer() {
    if (this.profile?.mediaSourceKind == "msk_tv" ?? true) {
      return false;
    }
    return true;
  }

  settings: Settings = {
    grouped: this.filter?.grouped || false,
    unitOfMeasure: this.unitOfMeasure,
    granularity: new GranularityHelper(
      "day",
      this.filter?.date.from || "",
      this.filter?.date.to || ""
    ),
  };

  loading = false;
  items: Array<Broadcaster> = [];

  get unitOfMeasureName() {
    switch (this.unitOfMeasure) {
      case "seconds":
        return "Secondi";
      case "spots":
        return "N° Spot";
      case "grp":
        return "GRP";
      case "grp_over_spots":
        return "GRP / Spot";
      case "audience":
        return "Contatti";
      case "ebiquity_min":
        return "€ (Stima Min.)";
      case "ebiquity_max":
        return "€ (Stima Max.)";
      default:
        return "Dati";
    }
  }

  get agencies(): BreakdownAgencyData[] {
    const agenciesMap = new Map();

    // eslint-disable-next-line
    this.items.forEach((item: any) => {
      let agency = item.agency as BreakdownAgencyData;

      if (agenciesMap.has(agency.id) == false) {
        agency.values = [];
        agency.broadcasters = [];
        agenciesMap.set(agency.id, agency);
      } else {
        agency = agenciesMap.get(agency.id);
      }
      agency.vodValues = [];

      let auditel = 1;
      if (
        ["grp", "grp_over_spots", "audience"].includes(
          this.settings.unitOfMeasure
        )
      ) {
        auditel = item.auditel;
      }

      const broadcaster: BreakdownBroadcasterData = {
        name: item.name,
        total: 0,
        values: item.timeIntervals.map((interval: IntervalCount) => {
          return this.getIntervalValue(interval);
        }),
        vodValues: item.timeIntervals.map((interval: IntervalCount) => {
          return interval.vodDetection;
        }),
        media: item.media.name.toLowerCase(),
        auditel,
        vod: item.timeIntervals.some((interval: IntervalCount) => {
          return interval.vodDetection;
        }),
      };

      if (item.logoUrls) {
        broadcaster.logoUrls = item.logoUrls;
      } else {
        broadcaster.logoUrls = [];
      }

      broadcaster.values.forEach((value, index) => {
        if (agency.values[index] == undefined) {
          agency.values[index] = 0;
        }
        if (agency.vodValues?.[index] == undefined) {
          agency.vodValues[index] = false;
        }
        agency.vodValues[index] = broadcaster.vodValues?.[index] ?? false;
        agency.values[index] += value;
      });

      broadcaster.total = broadcaster.values.reduce((previous, current) => {
        return previous + parseFloat(current.toFixed(2));
      }, 0);

      agency.broadcasters.push(broadcaster);
      agency.media = broadcaster.media;
    });

    const agencies = [...agenciesMap.values()];
    agencies.map((agency: BreakdownAgencyData) => {
      agency.total = agency.values.reduce((previous, current) => {
        return previous + parseFloat(current.toFixed(2));
      }, 0);

      agency.vod = agency.broadcasters.some(
        (broadcaster: BreakdownBroadcasterData) => {
          return broadcaster.vod;
        }
      );

      agency.broadcasters.sort(
        (a: BreakdownBroadcasterData, b: BreakdownBroadcasterData) => {
          if (a.total < b.total) {
            return 1;
          } else if (a.total > b.total) {
            return -1;
          }

          return 0;
        }
      );
    });

    agencies.sort((a: BreakdownAgencyData, b: BreakdownAgencyData) => {
      if (a.total < b.total) {
        return 1;
      } else if (a.total > b.total) {
        return -1;
      }

      return 0;
    });

    return agencies;
  }

  get tableData(): BreakdownData {
    const items: BreakdownDataItem[] = [];

    if (this.filter == undefined) {
      return {
        items: [],
        values: [],
        total: 0,
        vod: false,
        vodValues: [],
      };
    }

    if (this.filter?.media.radio) {
      const item = this.getBreakdownDataItem("radio");

      if (item) {
        items.push(item);
      }
    }

    if (this.filter?.media.tv) {
      const item = this.getBreakdownDataItem("tv");

      if (item) {
        items.push(item);
      }
    }

    const data: BreakdownData = {
      items: items,
      values: [],
      total: 0,
      vodValues: [],
      vod: false,
    };

    data.items.forEach((item: BreakdownDataItem) => {
      item.values.forEach((value, index) => {
        if (data.values[index] == undefined) {
          data.values[index] = value;
        } else {
          data.values[index] += value;
        }

        data.vodValues[index] = item.agencies.some(
          (agency: BreakdownAgencyData) => {
            return agency.vodValues[index];
          }
        );
      });
    });

    data.total = data.values.reduce((previous: number, current: number) => {
      return previous + parseFloat(current.toFixed(2));
    }, 0);

    data.vod = data.vodValues.some((value) => value);

    return data;
  }

  get tableDecimals(): number {
    if (this.unitOfMeasure == "grp" || this.unitOfMeasure == "grp_over_spots") {
      return 2;
    } else {
      return 0;
    }
  }

  get hasBothMedia(): boolean {
    return (this.filter?.media.tv && this.filter?.media.radio) || false;
  }

  get leftChartTitle(): string {
    const media =
      this.hasBothMedia || this.filter?.media.radio ? "Radio" : "TV";
    const type = this.hasBothMedia
      ? this.settings.grouped
        ? "Concessionarie"
        : "Emittenti"
      : "Concessionarie";

    return `${media}/${type}`;
  }

  get leftChartData(): CustomChartItem[] {
    const agencies = this.agencies.filter((agency: BreakdownAgencyData) => {
      if (agency.total == 0) {
        return false;
      }

      if (this.hasBothMedia) {
        return agency.media == "radio";
      } else {
        return true;
      }
    });

    if (this.settings.grouped || !this.hasBothMedia) {
      return agencies.map((agency: BreakdownAgencyData) => {
        return {
          label: agency.name,
          data: [parseFloat(agency.total.toFixed(2))],
        };
      });
    } else {
      const broadcasters = this.getTopBroadcasters("radio", 5);

      return broadcasters.map((broadcaster: BreakdownBroadcasterData) => {
        return {
          label: broadcaster.name,
          data: [parseFloat(broadcaster.total.toFixed(2))],
        };
      });
    }
  }

  get rightChartTitle(): string {
    const media =
      this.hasBothMedia || this.filter?.media.tv || false ? "TV" : "Radio";
    const type = this.hasBothMedia
      ? this.settings.grouped
        ? "Concessionarie"
        : "Emittenti"
      : "Emittenti";

    return `${media}/${type}`;
  }

  get rightChartData(): CustomChartItem[] {
    const media = this.hasBothMedia
      ? "tv"
      : this.filter?.media.radio
      ? "radio"
      : "tv";

    const agencies = this.agencies.filter((agency: BreakdownAgencyData) => {
      if (agency.total == 0) {
        return false;
      }

      if (this.hasBothMedia) {
        return agency.media == media;
      } else {
        return true;
      }
    });

    if (this.settings.grouped && this.hasBothMedia) {
      return agencies.map((agency: BreakdownAgencyData) => {
        return {
          label: agency.name,
          data: [parseFloat(agency.total.toFixed(2))],
        };
      });
    } else {
      const broadcasters = this.getTopBroadcasters(media, 5);

      return broadcasters.map((broadcaster: BreakdownBroadcasterData) => {
        return {
          label: broadcaster.name,
          data: [parseFloat(broadcaster.total.toFixed(2))],
        };
      });
    }
  }

  @NoCache
  get anyChartData(): boolean {
    const anyLeftChartData = this.leftChartData.some(
      (item: CustomChartItem) => item.data[0] > 0
    );
    const anyRightChartData = this.rightChartData.some(
      (item: CustomChartItem) => item.data[0] > 0
    );
    return anyLeftChartData || anyRightChartData;
  }

  get showCharts(): boolean {
    return (
      (this.charts &&
        !this.loading &&
        !this.onlyVodProfile &&
        this.anyChartData) ??
      false
    );
  }

  get applyAgentsFilter(): boolean {
    return (
      ProfileCapabilitiesService.agentsModuleEnabled(
        this.user.profile,
        this.agentUserInfo
      ) && !(this.agentUserInfo?.superUser ?? false)
    );
  }

  async load() {
    if (
      this.filter?.ready === false ||
      this.filter?.entry === null ||
      this.settings.granularity === null
    ) {
      return;
    }

    this.$emit("loading");
    this.loading = true;

    const data = await Promise.all([
      this.getBroadcastersBreakdown(this.getPostData(), requestTag),
      this.getCircuitsBreakdown(this.getPostData(), requestTag),
    ]);
    this.items = data.reduce(
      (arr: Model[], row: Model[]) => arr.concat(row),
      []
    );
    this.loading = false;

    this.$emit("loaded", this.getTotals());
  }

  protected getIntervalValue(interval: IntervalCount) {
    let value = 0;

    if (this.settings.unitOfMeasure == "seconds") {
      if (this.filter?.media.tv) {
        value += interval.tvSecondsCount as number;
      }
      if (this.filter?.media.radio) {
        value += interval.radioSecondsCount as number;
      }
    } else if (this.settings.unitOfMeasure == "spots") {
      if (this.filter?.media.tv) {
        value += interval.tvDetectionsCount as number;
      }
      if (this.filter?.media.radio) {
        value += interval.radioDetectionsCount as number;
      }
    } else if (this.settings.unitOfMeasure == "ebiquity_min") {
      if (this.filter?.media.radio) {
        value += (interval.ebiquityMin ?? 0) as number;
      }
    } else if (this.settings.unitOfMeasure == "ebiquity_max") {
      if (this.filter?.media.radio) {
        value += (interval.ebiquityMax ?? 0) as number;
      }
    } else if (this.settings.unitOfMeasure == "grp") {
      if (this.filter?.media.tv) {
        value += this.parseGrpCountValue(interval.grpCount);
      }
    } else if (this.settings.unitOfMeasure == "grp_over_spots") {
      if (this.filter?.media.tv) {
        value += this.parseGrpCountValue(
          (interval.grpCount as number) / interval.tvDetectionsCount
        );
      }
    } else if (this.settings.unitOfMeasure == "audience") {
      if (this.filter?.media.tv) {
        value += (interval.audienceCount as number) ?? 0;
      }
    }
    return value;
  }

  protected parseGrpCountValue(value: string | number | undefined): number {
    if (value && !isNaN(parseFloat(`${value}`))) {
      return parseFloat(`${value}`);
    } else {
      return 0.0;
    }
  }

  protected getBreakdownDataItem(media: string): BreakdownDataItem | null {
    const item: BreakdownDataItem = {
      agencies: this.agencies.filter(
        (agency: BreakdownAgencyData) => agency.media == media
      ),
      values: [],
      total: 0,
      media: media,
      vod: false,
      vodValues: [],
    };

    if (item.agencies.length) {
      item.agencies.forEach((agency: BreakdownAgencyData) => {
        agency.values.forEach((value, index) => {
          if (item.values[index] == undefined) {
            item.values[index] = value;
          } else {
            item.values[index] += value;
          }
        });
      });

      item.total = item.values.reduce((previous: number, current: number) => {
        return previous + parseFloat(current.toFixed(2));
      }, 0);

      return item;
    }

    return null;
  }

  protected getPostData() {
    const data: Dictionary = {
      date_range: [
        this.filter?.date.from as string,
        this.filter?.date.to as string,
      ],
    };

    if (this.filter?.entry) {
      if (this.filter?.spots && Array.isArray(this.filter?.spots)) {
        data.spot = this.filter?.spots;
      } else {
        switch (this.filter?.entry.type) {
          case "companies":
            data.company = this.filter?.entry.id;
            break;
          case "brands":
            data.brand = this.filter?.entry.id;
            break;
          case "products":
            data.product = this.filter?.entry.id;
            break;
        }
      }
    }
    let grpTarget = undefined;

    if (this.grpTargetId) {
      grpTarget = this.grpTargetId;
    }

    let postdata: any = {
      filter: data,
      granularity: this.settings.granularity.value,
      grpTarget: grpTarget,
    };

    if (this.applyAgentsFilter) {
      postdata = {
        ...postdata,
        agent_module_view: true,
      };
    }

    return postdata;
  }

  protected getTotals(): BreakdownTotals {
    const totals: BreakdownTotals = {
      spots: 0,
      seconds: 0,
      agencies: 0,
      broadcasters: 0,
      vodBroadcasters: 0,
      grp: 0.0,
      grpOverSpots: 0.0,
      ebiquityMin: 0,
      ebiquityMax: 0,
    };

    totals.agencies = this.tableData.items.reduce(
      (previous: number, item: BreakdownDataItem) => {
        return previous + item.agencies.length;
      },
      0
    );

    totals.broadcasters = this.tableData.items.reduce(
      (previous: number, item: BreakdownDataItem) => {
        return (
          previous +
          item.agencies.reduce(
            (previous: number, agency: BreakdownAgencyData) => {
              return (
                previous +
                agency.broadcasters.filter((broadcaster) => !broadcaster.vod)
                  .length
              );
            },
            0
          )
        );
      },
      0
    );

    totals.vodBroadcasters = this.tableData.items.reduce(
      (totalAcc: number, item: BreakdownDataItem) => {
        return (
          totalAcc +
          item.agencies.reduce(
            (vodAcc: number, agency: BreakdownAgencyData) => {
              return (
                vodAcc +
                agency.broadcasters.filter((broadcaster) => broadcaster.vod)
                  .length
              );
            },
            0
          )
        );
      },
      0
    );

    // eslint-disable-next-line
    this.items.forEach((item: any) => {
      item.timeIntervals.forEach((interval: IntervalCount) => {
        let spots = 0;
        let seconds = 0;
        let grp = 0.0;
        let grpOverSpots = 0.0;
        let ebiquityMin = 0.0;
        let ebiquityMax = 0.0;

        if (this.filter?.media.tv) {
          spots += interval.tvDetectionsCount;
          seconds += interval.tvSecondsCount;
          grp += this.parseGrpCountValue(interval.grpCount);
          grpOverSpots += this.parseGrpCountValue(
            (interval.grpCount as number) / interval.tvDetectionsCount
          );
        }

        if (this.filter?.media.radio) {
          spots += interval.radioDetectionsCount;
          seconds += interval.radioSecondsCount;
          ebiquityMin += interval.ebiquityMin ?? 0;
          ebiquityMax += interval.ebiquityMax ?? 0;
          totals.grp = 0;
        }

        totals.spots += spots;
        totals.seconds += seconds;
        totals.ebiquityMin += ebiquityMin;
        totals.ebiquityMax += ebiquityMax;

        totals.grp += grp ?? 0;
        totals.grpOverSpots = grpOverSpots ?? 0;
      });
    });
    if (this.filter?.media.radio) {
      totals.grp = 0;
      totals.grpOverSpots = 0;
    }
    return totals;
  }

  protected onGranularityChange(value: "day" | "sweek" | "month") {
    this.settings.granularity.value = value;
    this.load();
  }

  protected getTopBroadcasters(media: string, max = 5) {
    const broadcasters: BreakdownBroadcasterData[] = [];

    this.agencies.forEach((agency: BreakdownAgencyData) => {
      if (agency.media == media) {
        broadcasters.push(...agency.broadcasters);
      }
    });

    broadcasters.sort(
      (a: BreakdownBroadcasterData, b: BreakdownBroadcasterData) => {
        if (a.total > b.total) {
          return -1;
        } else if (a.total < b.total) {
          return 1;
        }

        return 0;
      }
    );

    const top = broadcasters.slice(0, max);

    if (broadcasters.length > max) {
      const other = broadcasters.slice(max, broadcasters.length - max);

      top.push({
        media: media,
        name: "Altro",
        total: other.reduce(
          (previous: number, current: BreakdownBroadcasterData) => {
            return previous + parseFloat(current.total.toFixed(2));
          },
          0
        ),
        values: [],
        auditel: 1,
        vodValues: [],
        vod: false,
      });
    }

    return top;
  }
}
